/**
 * WHERE "BOOK WITH THIS PACKAGE" GOES (plan item 2.12, 2026-10-23).
 *
 * The flow already existed — `/booking` spends a session and takes a package payment — but nothing pointed
 * AT it from the customer's own package. The dashboard's button opened the business's top page, and the
 * purchase-confirmation page offered nothing at all, so a customer who had just paid had to remember which
 * shop it was for and find their way back.
 *
 * THE RULE THIS ENCODES, in the order the confusions actually happen:
 *
 *  - NO SESSION IS SPENT HERE. This builds a URL and nothing else. A button that claimed a session on click
 *    would burn sessions on abandoned bookings; the booking is what spends it, when it is confirmed.
 *  - THE PACKAGE DECIDES WHAT IS COVERED, NOT WHAT MUST BE BOOKED. The first covered service is preselected
 *    because a link has to start somewhere, and the customer changes it in the normal service step.
 *  - A PROFESSIONAL IS REQUIRED, so the first one the package is subscribed to is used. `/booking` refuses to
 *    load without `staffId`, and inventing a professional from the shop's roster would be offering somebody
 *    who never agreed to the package. If the package names none, that is a reason, not a guess.
 *  - WHEN IT CANNOT BE BUILT, IT SAYS WHY. Every early return carries a sentence a customer can act on; the
 *    callers render that sentence instead of a button that would fail.
 *
 * @param {object} purchase a package purchase, from `/api/packages/customer/purchases` (or the confirm payload)
 * @returns {{ok: true, href: string} | {ok: false, reason: string}}
 */

/** First covered service id on the purchase's own snapshot — what was bought, not what the package says today. */
function firstCoveredServiceId(purchase) {
  const lines = purchase?.packageSnapshot?.services;
  if (!Array.isArray(lines)) return null;
  for (const line of lines) {
    const id = line?.serviceId?._id || line?.serviceId;
    if (id) return String(id);
  }
  return null;
}

/** First professional the package is subscribed to. An id or a populated document both work. */
function firstSubscribedProfessionalId(purchase) {
  const ids = purchase?.packageId?.subscribedStaffIds;
  if (!Array.isArray(ids)) return null;
  for (const entry of ids) {
    const id = entry?._id || entry;
    if (id) return String(id);
  }
  return null;
}

function shopIdOf(purchase) {
  const shop = purchase?.shopId;
  const id = shop?._id || shop;
  return id ? String(id) : null;
}

export function isPackageExpired(purchase, now = Date.now()) {
  const until = purchase?.validUntil ? new Date(purchase.validUntil) : null;
  return Boolean(until && !Number.isNaN(until.getTime()) && until.getTime() < now);
}

/** Sessions left, from the server's number when it sent one, never invented. */
export function sessionsRemainingOn(purchase) {
  const raw =
    purchase?.sessionsRemaining ??
    (Number(purchase?.totalSessions) || 0) - (Number(purchase?.sessionsUsed) || 0);
  const value = Number(raw);
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

export function buildPackageBookingLink(purchase) {
  if (!purchase || (!purchase._id && !purchase.id)) {
    return { ok: false, reason: "We could not find that package." };
  }

  if (isPackageExpired(purchase)) {
    return {
      ok: false,
      reason: "This package has expired — reactivate it first.",
    };
  }

  if (sessionsRemainingOn(purchase) <= 0) {
    return {
      ok: false,
      reason: "No sessions are left on this package.",
    };
  }

  const shopId = shopIdOf(purchase);
  if (!shopId) {
    return {
      ok: false,
      reason: "This package does not record the shop that sold it.",
    };
  }

  const serviceId = firstCoveredServiceId(purchase);
  if (!serviceId) {
    return {
      ok: false,
      reason:
        "This package does not list a service that can be booked — contact the shop.",
    };
  }

  const staffId = firstSubscribedProfessionalId(purchase);
  if (!staffId) {
    return {
      ok: false,
      reason:
        "The shop has not assigned a professional to this package yet — contact the shop.",
    };
  }

  // `packageId` is the PURCHASE id: that is what `/booking` reads to preselect the package, and it is what
  // ties the booking's payment to the right purchase.
  const params = new URLSearchParams({
    shopId,
    staffId,
    serviceId,
    packageId: String(purchase._id || purchase.id),
  });

  return { ok: true, href: `/booking?${params.toString()}` };
}
